// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import createQuote from "@bg/App/Handlers/CustomQuote/CreateCustomQuote";
import renameQuote from "@bg/App/Handlers/CustomQuote/RenameCustomQuote";
import { CustomQuoteModel } from "@bg/App/Services/CustomQuoteService";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import addWishList from "@insite/client-framework/Store/Pages/MyLists/Handlers/AddWishList";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quote?: CustomQuoteModel;
    onCancel: () => void;
    onSubmit: () => void;
}

interface State {
    name: string;
    nameError?: React.ReactNode;
    isLoading: boolean;
}

const mapDispatchToProps = {
    addWishList,
    updateWishList,
    createQuote,
    renameQuote,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsEditListFormStyles {
    container?: GridContainerProps;
    nameGridItem?: GridItemProps;
    nameTextField?: TextFieldProps;
    descriptionGridItem?: GridItemProps;
    descriptionTextField?: TextFieldProps;
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    createButton?: ButtonPresentationProps;
}

export const myListsEditListFormStyles: MyListsEditListFormStyles = {
    nameGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    cancelButton: {
        color: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
};

const styles = myListsEditListFormStyles;

class MyListsEditListForm extends React.Component<Props, State> {
    static contextType = ToasterContext;
    context!: React.ContextType<typeof ToasterContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            name: props.quote?.name || "",
            isLoading: false,
        };
    }

    submitHandler = (e: any) => {
        if (this.state.name === "") {
            this.setState({ nameError: translate("Quote Name is Required") });
            return;
        }

        if (this.props.quote) {
            this.setState({ isLoading: true });
            this.props.renameQuote({
                customQuoteId: this.props.quote.id,
                newName: this.state.name,
                onSuccess: this.onSubmitSuccess,
                onError: this.onSubmitError,
                onComplete(resultProps: any) {
                    if (resultProps?.successful) {
                        this.onSuccess?.();
                    } else if (!resultProps.apiResult.successful) {
                        this.onError?.(resultProps.apiResult.error);
                    }
                },
            });
        } else {
            this.props.createQuote({
                name: this.state.name,
                onSuccess: this.onSubmitSuccess,
                onError: this.onSubmitError,
                onComplete(resultProps: any) {
                    if (resultProps.apiResult?.successful) {
                        this.onSuccess?.();
                    } else if (!resultProps.apiResult?.successful) {
                        this.onError?.(resultProps.apiResult.error);
                    }
                },
            });
        }
    };

    onSubmitSuccess = () => {
        this.setState({ isLoading: false });
        this.props.onSubmit();
        this.context.addToast({
            body: translate(`Quote ${this.props.quote ? "Updated" : "Created"}`),
            messageType: "success",
        });
    };

    onSubmitError = (errorMessage: string) => {
        this.setState({ isLoading: false });
        this.setState({ nameError: errorMessage });
    };

    nameChangeHandler = (e: any) => {
        this.setState({
            name: e.target.value,
            nameError: e.target.value.length > 100 ? translate("Max Quote Name length is 100") : "",
        });
    };

    render() {
        const { name, nameError, isLoading } = this.state;
        return (
            <LoadingOverlay loading={isLoading}>
                <GridContainer {...styles.container} data-test-selector="editCustomQuoteForm">
                    <GridItem {...styles.nameGridItem}>
                        <TextField
                            data-test-selector="myCusotmQuotesEditFormName"
                            label={translate("Quote Name")}
                            {...styles.nameTextField}
                            name="name"
                            error={nameError}
                            required
                            maxLength={50}
                            defaultValue={name}
                            onChange={this.nameChangeHandler}
                        ></TextField>
                    </GridItem>
                    <GridItem {...styles.buttonsGridItem} className="modal-actions">
                        <Button
                            {...styles.createButton}
                            onClick={this.submitHandler}
                            disabled={name.length > 100}
                            data-test-selector="myCusotmQuotesEditFormSubmit"
                        >
                            {translate(this.props.quote ? "Save" : "Create Quote")}
                        </Button>
                        <Button {...styles.cancelButton} onClick={this.props.onCancel}>
                            {translate("Cancel")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </LoadingOverlay>
        );
    }
}

export default connect(null, mapDispatchToProps)(MyListsEditListForm);
